import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Autoplay } from "swiper/modules";
import "swiper/css/pagination";

import { Container } from "../Container/Container";
import { useLocation } from "react-router-dom";
import { schoolArr } from "../Studies/ourSchoolObj";
import { routes } from "../../shared/routes/routes";
import { useEffect, useMemo } from "react";

import {
  PageOuter,
  PageWrapper,
  BackLink,
  HeroBlock,
  Badge,
  Title,
  Accent,
  SliderBlock,
  SliderImg,
  ContentCard,
  LeadParagraph,
  Paragraph,
  Quote,
  BulletList,
  BulletItem,
  NotFoundBlock,
} from "./styled";

type SchoolKey = keyof typeof schoolArr;

type Block =
  | { kind: "para"; text: string }
  | { kind: "bullets"; items: string[] }
  | { kind: "quote"; text: string };

const isBullet = (s: string) => /^[-—•]\s+/.test(s.trim());
const stripBullet = (s: string) => s.trim().replace(/^[-—•]\s+/, "");
const isShortQuote = (s: string) => {
  const t = s.trim();
  return /^[«"].+[»"]\.?$/.test(t) && t.length < 160;
};

const groupBlocks = (lines: string[]): Block[] => {
  const blocks: Block[] = [];
  let bulletBuf: string[] = [];

  const flushBullets = () => {
    if (bulletBuf.length) {
      blocks.push({ kind: "bullets", items: bulletBuf });
      bulletBuf = [];
    }
  };

  for (const line of lines) {
    if (isBullet(line)) {
      bulletBuf.push(stripBullet(line));
    } else {
      flushBullets();
      if (isShortQuote(line)) {
        blocks.push({ kind: "quote", text: line.trim() });
      } else {
        blocks.push({ kind: "para", text: line });
      }
    }
  }
  flushBullets();
  return blocks;
};

export const OurSchoolSlug = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const location = useLocation();
  const key = location.pathname.split("/")[2] as SchoolKey;
  const schoolObject = schoolArr[key];

  const blocks = useMemo(
    () => (schoolObject ? groupBlocks(schoolObject.info) : []),
    [schoolObject]
  );

  if (!schoolObject) {
    return (
      <Container>
        <PageOuter>
          <NotFoundBlock>Раздел не найден.</NotFoundBlock>
        </PageOuter>
      </Container>
    );
  }

  const [leadBlock, ...restBlocks] = blocks;
  const leadText =
    leadBlock && leadBlock.kind === "para" ? leadBlock.text : null;
  const contentBlocks = leadText ? restBlocks : blocks;

  return (
    <Container>
      <PageOuter>
        <PageWrapper>
          <BackLink to={routes.ourSchool}>
            <svg viewBox="0 0 14 10" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M13 5H1M1 5L5 1M1 5L5 9"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
              />
            </svg>
            Все ступени
          </BackLink>

          <HeroBlock>
            <Badge>Наша школа</Badge>
            <Title>{schoolObject.title}</Title>
            <Accent />
          </HeroBlock>

          {schoolObject.photos && schoolObject.photos.length > 0 && (
            <SliderBlock>
              <Swiper
                pagination={{ clickable: true }}
                modules={[Pagination, Autoplay]}
                autoplay={{ delay: 6000, disableOnInteraction: false }}
                loop
              >
                {schoolObject.photos.map((value: any, i: number) => (
                  <SwiperSlide key={i}>
                    <SliderImg src={value} alt={schoolObject.title} />
                  </SwiperSlide>
                ))}
              </Swiper>
            </SliderBlock>
          )}

          {blocks.length > 0 && (
            <ContentCard>
              {leadText && <LeadParagraph>{leadText}</LeadParagraph>}
              {contentBlocks.map((block, idx) => {
                if (block.kind === "bullets") {
                  return (
                    <BulletList key={idx}>
                      {block.items.map((item, i) => (
                        <BulletItem key={i}>{item}</BulletItem>
                      ))}
                    </BulletList>
                  );
                }
                if (block.kind === "quote") {
                  return <Quote key={idx}>{block.text}</Quote>;
                }
                return <Paragraph key={idx}>{block.text}</Paragraph>;
              })}
            </ContentCard>
          )}
        </PageWrapper>
      </PageOuter>
    </Container>
  );
};
